import React from 'react';

import { AccountTransactionDetailsItem } from '@/components/molecules/AccountTransactionDetailsItem';
import { AmountTransactionDetailsItem } from '@/components/molecules/AmountTransactionDetailsItem';
import { DetailsDrawerFees } from '@/components/organisms/DetailsDrawerFees';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useTranslator } from '@/hooks/useTranslator';
import { BitsoBank } from '@/types/bitsoBank';
import { PaymentRailType } from '@/types/currency';
import { DetailsField } from '@/types/detailsField';
import { FiatAccountType } from '@/types/fiatAccountType';
import { Transaction } from '@/types/transaction';
import { isSwiftLocalInfo } from '@/types/transaction.ext';

export const useTransactionDetailsFields = (
  transaction: Transaction,
  fromBanks: readonly BitsoBank[],
  toBanks: readonly BitsoBank[],
): ReadonlyArray<DetailsField | null> => {
  const { transactionDetails, date, localInfo } = transaction;

  const dateFormatter = useDateFormatter('MMM d, yyyy | hh:mm a');
  const tr = useTranslator();
  // TODO: replace this when the endpoint gets fixed
  const fixedDateString = React.useMemo((): string => `${date.replace(' ', 'T')}Z`, [date]);
  const uetr = React.useMemo((): string | null => {
    return isSwiftLocalInfo(localInfo) ? localInfo.uetr : null;
  }, [localInfo]);

  return React.useMemo(
    (): ReadonlyArray<DetailsField | null> =>
      transaction
        ? [
            {
              key: 'type',
              label: tr('TRANSACTION_TYPE'),
              value:
                tr(transaction.type) +
                (emptyPaymentTypes.includes(transaction.paymentType)
                  ? ''
                  : `: ${tr(transaction.paymentType)}`),
            },
            {
              key: 'date',
              label: tr('DATE_AND_TIME'),
              value: dateFormatter.parseAndFormat(fixedDateString),
            },
            {
              key: 'amount',
              label: tr('AMOUNT'),
              value: (
                <AmountTransactionDetailsItem
                  from={transaction.fromAmount}
                  to={transaction.toAmount}
                  conversionDetails={transaction.transactionDetails.conversionDetails}
                />
              ),
            },
            {
              key: 'fees',
              label: tr('FEES'),
              value: (
                <DetailsDrawerFees
                  totalAmount={transaction.fromAmount}
                  feeDetails={transaction.transactionDetails.feeDetails}
                  fromCurrency={transaction.fromAmount.currencyCode}
                />
              ),
            },
            transaction.fromAccount
              ? {
                  key: 'from',
                  label: tr('FROM'),
                  value: (
                    <AccountTransactionDetailsItem
                      account={transaction.fromAccount}
                      banks={fromBanks}
                    />
                  ),
                }
              : null,
            transaction.toAccount
              ? {
                  key: 'to',
                  label: tr('TO'),
                  value: (
                    <AccountTransactionDetailsItem
                      account={transaction.toAccount}
                      banks={toBanks}
                    />
                  ),
                }
              : null,
            transactionDetails.paymentReference
              ? {
                  key: 'paymentReference',
                  label: tr('PAYMENT_REFERENCE_DETAILS_LABEL'),
                  value: transactionDetails.paymentReference,
                  title: transactionDetails.paymentReference,
                  columnSpan: 2,
                }
              : null,
            {
              key: 'id',
              label: uetr ? tr('TRANSACTION_UETR') : tr('TRANSACTION_ID'),
              value: uetr ?? transaction.id,
              title: uetr ?? transaction.id,
              columnSpan: 2,
            },
          ]
        : [],
    [
      dateFormatter,
      fixedDateString,
      fromBanks,
      toBanks,
      tr,
      transaction,
      transactionDetails.paymentReference,
      uetr,
    ],
  );
};

const emptyPaymentTypes = [FiatAccountType.unset, PaymentRailType.LocalExchange];
